import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';

import { Bar } from 'react-chartjs-2';
import * as api_dash from '../../api/Dashboard.tsx';
import React, { useEffect } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import { Checkbox, ListItemText } from '@mui/material';
import { useDependencies } from '../../misc/DependenciesProvider.tsx';
import * as frac_api from "../../api/FracApi.ts";


ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

export var LoggedIn = false;

const colours = [
    "#25CCF7", "#FD7272", "#54a0ff", "#00d2d3",
    "#1abc9c", "#2ecc71", "#3498db", "#9b59b6", "#34495e",
    "#16a085", "#27ae60", "#2980b9", "#8e44ad", "#2c3e50",
    "#f1c40f", "#e67e22", "#e74c3c", "#ecf0f1", "#95a5a6",
    "#f39c12", "#d35400", "#c0392b", "#bdc3c7", "#7f8c8d",
    "#55efc4", "#81ecec", "#74b9ff", "#a29bfe", "#dfe6e9",
    "#00b894", "#00cec9", "#0984e3", "#6c5ce7", "#ffeaa7",
    "#fab1a0", "#ff7675", "#fd79a8", "#fdcb6e", "#e17055",
    "#d63031", "#feca57", "#5f27cd", "#54a0ff", "#01a3a4"
]



export const options = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top' as const,
        },
        title: {
            display: true,
        },
    },
};
export interface Dataset {
    label: string;
    data: number[];
    backgroundColor: string;
}

export interface Datasets {
    labels: string[];
    datasets: Dataset[];
}

export function DashboardPageBar() {
    const [pestCountResp, setPestCountResp] = React.useState<frac_api.PestCountPerMoaResponse | null>(null);

    const [data, setData] = React.useState<Datasets | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [moaCodes, setMoaCodes] = React.useState<string[] | null>(null);
    const [selectedMoaCode, setSelectedMoaCode] = React.useState<string | null>(null);
    const [pests, setPests] = React.useState<Map<string, number> | null>(null);
    const [selectedPests, setSelectedPests] = React.useState<string[]>([]);
    const [frequencies, setFrequencies] = React.useState<string[] | null>(null)
    const [selectedFrequencies, setSelectedFrequencies] = React.useState<string[]>([]);
    const { fracAPI } = useDependencies();

    useEffect(() => {
        const getMoaCodes = async () => {
            const dashData = await api_dash.getDashboardData();

            setMoaCodes(dashData.moaCodes);
        }

        getMoaCodes();
    }, []);

    // get the data based on user selection of moa code
    useEffect(() => {

        if (!selectedMoaCode) {
            return;
        }

        const getData = async () => {
            setIsLoading(true);

            try {
                const filter = new frac_api.ChartFilter({
                    moa_code: selectedMoaCode,
                });

                const resp = await fracAPI.getPestCountPerMoa(filter);
                setPestCountResp(resp);

                // pests
                let pestNameToId: Map<string, number> = new Map();
                resp.results.forEach((pest) => {
                    pestNameToId.set(pest.scientific_name, pest.pest_id);
                })
                setPests(pestNameToId);
                setSelectedPests(Array.from(pestNameToId.keys()));

                // freqs
                setFrequencies(resp.all_frequencies);
                setSelectedFrequencies(resp.selected_frequencies);
            } finally {
                setIsLoading(false);
            }
        }

        getData();
    }, [selectedMoaCode]);

    // if the user selects pests, update the data
    useEffect(() => {

        if (!selectedMoaCode || !pests) {
            return;
        }

        const getData = async () => {
            setIsLoading(true);

            try {
                let pestIds: number[] | null = null;
                if (selectedPests.length > 0) {
                    pestIds = selectedPests.map((name) => pests.get(name)).filter((x) => x != null);
                }

                const filter = new frac_api.ChartFilter({
                    moa_code: selectedMoaCode,
                    pest_ids: pestIds,
                    freq_resistance: selectedFrequencies
                });

                const resp = await fracAPI.getPestCountPerMoa(filter);
                setPestCountResp(resp);

            } finally {
                setIsLoading(false);
            }
        }

        getData();
    }, [selectedPests, selectedFrequencies]);

    // create the graph data
    useEffect(() => {
        if (!pestCountResp || !pestCountResp.results) {
            return;
        }

        let yearsSet: Set<number> = new Set();
        pestCountResp.results.forEach(pest => {
            pest.details.forEach(detail => {
                yearsSet.add(detail.year);
            });
        })

        let yearsNumbers: number[] = Array.from(yearsSet).sort();
        const yearLabels = yearsNumbers.map(year => year.toString());

        let sciNamesSet: Set<string> = new Set();
        pestCountResp.results.forEach(pest => {
            sciNamesSet.add(pest.scientific_name);
        });

        let sciNames: string[] = Array.from(sciNamesSet).sort();

        const datasets: Datasets = {
            labels: sciNames,
            datasets: []
        };

        let num = 0;
        let yearToDataset: Map<number, Dataset> = new Map();
        for (const year of yearsNumbers) {
            const dataset: Dataset = {
                label: year.toString(),
                data: [],
                backgroundColor: colours[num % colours.length]
            };

            datasets.datasets.push(dataset);
            yearToDataset.set(year, dataset);
            num = num + 1;
        }

        for (let i = 0; i < pestCountResp.results.length; i++) {
            let pest = pestCountResp.results[i];
            for (let j = 0; j < pest.details.length; j++) {
                let detail = pest.details[j];

                const color = colours[i % colours.length];

                let dataset = yearToDataset.get(detail.year)
                if (dataset) {
                    dataset.data.push(detail.count);
                }

            }
        }

        setData(datasets);

    }, [pestCountResp]);

    const handleSetPests = (value) => {
        setSelectedPests(
            typeof value === 'string' ? value.split(',') : value,
        );
    };

    const handleSetFrequencies = (value) => {
        const new_values = typeof value === 'string' ? value.split(',') : value;

        if (new_values.length > 0) {
            setSelectedFrequencies(
                typeof value === 'string' ? value.split(',') : value,
            );
        }
    }

    return (
        <div>
            {isLoading && <LinearProgress />}

            {!isLoading && data &&
                <Bar options={options} data={data} />
            }

            <br />


            <>

                <FormControl >
                    <InputLabel id="frac-moa-code-label">FRAC MoA Code</InputLabel>
                    <Select
                        labelId="frac-moa-code-label"
                        id="moa-code"
                        value={selectedMoaCode}
                        label="FRAC MoA Code"
                        onChange={(e) => setSelectedMoaCode(e.target.value as string)}
                        style={{ width: '300px' }}
                    >
                        {
                            moaCodes && moaCodes.map((c) => {
                                return <MenuItem value={c}>{c}</MenuItem>
                            })}

                    </Select>


                </FormControl>

                {pests &&
                    <FormControl >
                        <InputLabel id="pest-label">Pest</InputLabel>
                        <Select
                            labelId="pest-label"
                            id="pest"
                            value={selectedPests}
                            label="Pest"
                            multiple
                            renderValue={(selected) => selected.join(', ')}
                            onChange={(e) => handleSetPests(e.target.value)}
                            style={{ width: '300px' }}
                        >
                            {
                                pests && Array.from(pests.keys()).map((c) => {
                                    return <MenuItem key={c} value={c}>
                                        <Checkbox checked={selectedPests.indexOf(c) > -1} />
                                        <ListItemText primary={c} />
                                    </MenuItem>
                                })}

                        </Select>

                    </FormControl>
                }

                {frequencies &&
                    <FormControl >
                        <InputLabel id="freq-label">Frequency</InputLabel>
                        <Select
                            labelId="freq-label"
                            id="freq"
                            value={selectedFrequencies}
                            label="Frequency"
                            multiple
                            renderValue={(selected) => selected.join(', ')}
                            onChange={(e) => handleSetFrequencies(e.target.value)}
                            style={{ width: '300px' }}
                        >
                            {
                                frequencies && frequencies.map((c) => {
                                    return <MenuItem key={c} value={c}>
                                        <Checkbox checked={selectedFrequencies.indexOf(c) > -1}

                                            disabled={selectedFrequencies.length === 1 && selectedFrequencies.indexOf(c) > -1}

                                        />
                                        <ListItemText primary={c} />
                                    </MenuItem>
                                })}

                        </Select>
                    </FormControl>
                }

            </>
        </div>
    )
}